import { useCallback, useMemo } from 'react';

import AutocompleteWithChips from 'components/AutocompleteWithChips/AutocompleteWithChips';
import { LookupDateRangeQueryItemDto } from 'types/CodegenLookupTypes';
import { SelectedItemDto } from 'types/CodegenReportTypes';
import { usePostLookupBatterSessions, usePostLookupPitcherSessions } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';
import { displayDate } from 'utilities/dateHelpers';

type Props = {
  playerType: 'Batter' | 'Pitcher';
  dateRanges: LookupDateRangeQueryItemDto[] | undefined;
  team: SelectedItemDto | undefined;
  playerIds: string[] | undefined;
} & (
  | {
      single?: false;
      selectedSession: SelectedItemDto[] | undefined;
      onChange: (session: SelectedItemDto[]) => void;
    }
  | {
      single: true;
      selectedSession: SelectedItemDto | undefined;
      onChange: (session: SelectedItemDto) => void;
    }
);

const SelectPlayerSession = ({ playerType, single, selectedSession, onChange, dateRanges, team, playerIds }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } =
    playerType === 'Batter'
      ? usePostLookupBatterSessions(dateRanges, team?.id, playerIds)
      : usePostLookupPitcherSessions(dateRanges, team?.id, playerIds);

  const allSessionIds = useMemo(() => data?.map((datum) => datum.id) ?? [], [data]);

  const formatGame = (game: string): string => {
    const teamNames = game.split(' vs ');
    const againstTeamName = teamNames.filter((p) => p !== team?.name)[0] || team?.name;
    return `vs ${againstTeamName}`;
  };

  const getSessionName = useCallback(
    (sessionId: string) => {
      const sessionNames = new Map(
        data?.map((datum) => [datum.id, `${displayDate(datum.gameDate)}${datum.game.length > 4 ? ` | ${formatGame(datum.game)}` : ''}`])
      );
      sessionNames.set('', ''); // needed by autocomplete to reset properly
      return sessionNames.get(sessionId) ?? (t('Nonexistent session') as string);
    },
    [data]
  );

  const getSessionFromId = useCallback(
    (id: string): SelectedItemDto => {
      const sessionData = data?.find((d) => d.id === id);
      return sessionData ? { id: sessionData.id, name: getSessionName(sessionData.id) } : { id, name: getSessionName(id) };
    },
    [data, getSessionName]
  );

  if (single) {
    return (
      <AutocompleteWithChips
        label={t('Sessions')}
        options={allSessionIds}
        selected={selectedSession?.id ?? ''}
        setSelected={(selectedId) => onChange({ id: selectedId, name: getSessionName(selectedId) })}
        getOptionLabel={(option) => (isLoading ? t('Loading sessions...') : getSessionName(option))}
        noOptionsText={t(isLoading ? t('Loading sessions...') : t('No sessions found'))}
        disabled={!dateRanges || !team?.id || !playerIds}
        single
      />
    );
  }

  return (
    <AutocompleteWithChips
      label={t('Sessions')}
      options={allSessionIds}
      selected={selectedSession?.map((s) => s.id) ?? []}
      setSelected={(selectedIds) => onChange(selectedIds.map(getSessionFromId))}
      getOptionLabel={(option) => (isLoading ? t('Loading sessions...') : getSessionName(option))}
      noOptionsText={isLoading ? t('Loading sessions...') : t('No sessions found')}
      disabled={!dateRanges || !team?.id || !playerIds}
      disableCloseOnSelect={allSessionIds.length > 1}
    />
  );
};

export default SelectPlayerSession;
